import React, { FC } from "react";
import Table from "@mui/material/Table";
import {
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import SearchBar from "../../Table/SearchBar/SearchBar";
import ConfirmationModal from "../../Table/Modals/ConfirmationModal";
import ServiceTableHead from "../ServiceTableHead";
import TableToolbar from "../../Table/Toolbar/TableToolbar";
import AptChangeModal from "../Modals/AptChange/AptChangeModal";
import { StyledCheckbox } from "../../Table/Cells/StyledCheckbox";
import { ServiceRepairObj } from "../../../../GlobalTypes";
import { styled } from "@mui/material/styles";
import useTableSelector from "./useTableSelector";

const StyledRootDiv = styled("div")({
  width: "100%",
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  marginBottom: theme.spacing(2),
  position: "relative",
}));

const StyledViewQuote = styled(Typography)({
  "&:hover": {
    cursor: "pointer",
  },
  textDecoration: "underline",
});

const StyledTypography = styled(Typography)({
  display: "flex",
  justifyContent: "flex-start",
  padding: 10,
});

const StyledTypographyBold = styled(Typography)({
  display: "flex",
  color: "red",
  justifyContent: "flex-start",
  padding: 10,
  fontWeight: "bold",
});

const StyledBackdrop = styled(Backdrop)({
  position: "absolute",
  width: "100%",
  height: "50vh",
  zIndex: 10,
});

interface ServiceTableProps {
  viewSR: (row: ServiceRepairState) => void;
}

export interface ServiceRepairState extends ServiceRepairObj {
  id: string;
}

const ServiceTable: FC<ServiceTableProps> = ({ viewSR }) => {
  const {
    pageService,
    rowsPerPageService,
    isServiceSearch,
    filterByService,
    selected,
    searchTermService,
    error,
    isFetching,
    data,
    role,
    hasAdminAccess,
    handleChange,
    handleFilterByChange,
    onKeyDown,
    handleSearchBtn,
    handleReset,
    mutation,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheck,
    unCheckAll,
    hoverId,
    openModal,
    mouseEnter,
    mouseLeave,
    handleCloseModal,
    handleOpenModal,
    openApt,
    aptId,
    handleCloseApt,
    handleOpenApt,
    isSelected,
    setSearch,
  } = useTableSelector();

  if (error)
    return <div>An error has occured, please try refreshing the page.</div>;

  return (
    <>
      {!isFetching && data ? (
        <SearchBar
          showTab="Service Repair"
          searchTerm={searchTermService}
          filterBy={filterByService}
          handleFilterByChange={handleFilterByChange}
          handleReset={handleReset}
          onKeyDown={onKeyDown}
          handleSearchBtn={handleSearchBtn}
          handleChange={handleChange}
        />
      ) : null}

      <StyledRootDiv>
        {!isFetching && data === "Must be Number" && (
          <StyledTypographyBold>
            Search Term must be a Number
          </StyledTypographyBold>
        )}

        <AptChangeModal
          open={openApt}
          handleClose={handleCloseApt}
          serviceId={aptId}
          setSearch={setSearch}
        />

        <ConfirmationModal
          mutation={mutation}
          handleClose={handleCloseModal}
          open={openModal}
          showTab="Service Repair"
        />

        <StyledPaper style={{ position: "relative" }}>
          {hasAdminAccess && (
            <TableToolbar
              unCheckAll={unCheckAll}
              handleOpenModal={handleOpenModal}
              showTab={"Service Repair"}
              numSelected={selected.length}
              handleOpenApt={handleOpenApt}
              searchTerm={searchTermService}
              searchTermFilter={filterByService}
              isSearch={isServiceSearch}
            />
          )}

          {isFetching ? (
            <StyledBackdrop open={isFetching}>
              <CircularProgress color="primary" />
            </StyledBackdrop>
          ) : (
            <>
              {data && data.currentPage && data.currentPage.length ? (
                <>
                  <TableContainer>
                    <Table
                      style={{ minWidth: 750 }}
                      aria-labelledby="tableTitle"
                      size={"small"}
                      aria-label="enhanced table"
                    >
                      <ServiceTableHead />

                      <TableBody>
                        {data.currentPage.map((row: ServiceRepairObj) => {
                          const isItemSelected = isSelected(row._id);
                          const labelId = `enhanced-table-checkbox-${row._id}`;

                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={row._id}
                              onMouseEnter={() => mouseEnter(row._id)}
                              onMouseLeave={mouseLeave}
                            >
                              {hasAdminAccess && (
                                <TableCell padding="checkbox">
                                  {row._id === hoverId || selected.length ? (
                                    <StyledCheckbox
                                      onClick={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                      ) => handleCheck(event, row._id)}
                                      checked={isItemSelected}
                                      color="primary"
                                      inputProps={{
                                        "aria-labelledby": labelId,
                                      }}
                                    />
                                  ) : (
                                    <div style={{ width: 16, height: 16 }} />
                                  )}
                                </TableCell>
                              )}
                              <TableCell
                                onClick={() =>
                                  viewSR(row as ServiceRepairState)
                                }
                                padding="checkbox"
                              >
                                <StyledViewQuote variant="caption">
                                  View
                                </StyledViewQuote>
                              </TableCell>

                              <TableCell
                                align="center"
                                scope="row"
                                padding="none"
                              >
                                {row.createdAt || "N/A"}
                              </TableCell>
                              <TableCell
                                align="center"
                                scope="row"
                                padding="none"
                              >
                                {row.timeCreated || "N/A"}
                              </TableCell>

                              <TableCell align="center">
                                {row.customerName}
                              </TableCell>
                              {(hasAdminAccess || role === "Outside Sales") && (
                                <TableCell align="center">
                                  {row.dealer}
                                </TableCell>
                              )}
                              <TableCell align="center">
                                {row.snNumber}
                              </TableCell>
                              <TableCell align="center">
                                {row.poNumber || "N/A"}
                              </TableCell>
                              <TableCell align="center">
                                {row.soNumber || "N/A"}
                              </TableCell>
                              <TableCell align="center">
                                {row.appointment || "N/A"}
                              </TableCell>

                              {(hasAdminAccess || role === "Outside Sales") && (
                                <TableCell align="center">
                                  {row.team ? `Team ${row.team}` : "N/A"}
                                </TableCell>
                              )}

                              <TableCell align="center">{row.status}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[15, 20, 25]}
                    component="div"
                    count={data.totalResults}
                    rowsPerPage={rowsPerPageService}
                    page={pageService}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              ) : (
                <StyledTypography>No Service Repairs Found</StyledTypography>
              )}
            </>
          )}
        </StyledPaper>
      </StyledRootDiv>
    </>
  );
};

export default ServiceTable;
